<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form">				
				<h2>Категории сервисных работ</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddCategoryClick">
					Новая категория
				</el-button>
			</div>
		</el-col>
	</el-row>
	
	<div class="base-form">			
		<el-row>
			<el-col :span="24">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" clearable placeholder="Поиск по названию категории сервисных работ"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</el-col>
		</el-row>
	</div>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 				
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"				
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">
				<template #empty>
					Список категорий пуст, попробуйте изменить фильтр
				</template>
				<el-table-column v-if="!isPickerMode" type="selection" />
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onPickJobCategoryClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Категория" prop="name" sortable column-key="name" />
				<el-table-column label="Стоимость НЧ" prop="costPerHour" sortable column-key="costPerHour" />
				<el-table-column label="Описание" prop="description" column-key="description" />		
				<el-table-column v-if="!isPickerMode" align="right">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onDeleteJobCategoryClick(scope.row.id, scope.row.name, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
	<el-row v-if="isPickerMode">
		<el-col>
			<div class="base-form">
				<el-button @click="onCloseCurrentTabClick">
					Отмена
				</el-button>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ServiceJobCategoryModel from '@/models/ServiceJobCategoryModel';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class ServiceJobCategoriesView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	isPickerMode = false;
	onPickedCallback: Function | null = null;
	circleIcon = CirclePlus;
	searchIcon = Search;
	
	/** view-models */
	tableLoading = false;
	tableData: Array<ServiceJobCategoryModel> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';

	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.fetchJobCategoriesAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobCategoriesAsync()
		);

		EventHelper.setEventListener('jobCategoriesUpdate', () => this.fetchJobCategoriesAsync());
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		EventHelper.removeEventListener('jobCategoriesUpdate', () => this.fetchJobCategoriesAsync());
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}
	}

	onTableRowClick(row: ServiceJobOperationModel) {
		if (this.isPickerMode) {
			return;
		}
		const viewTab = TabsDictionary.jobCategoryView;		
		viewTab.params = [ row, false ];
		store.commit('addTab', viewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddCategoryClick() {
		const addTab = TabsDictionary.jobCategoryAdd;
		const addNewCategory = true;
		addTab.params = [ null, addNewCategory ];
		store.commit('addTab', addTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobCategoriesAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobcategories', { params: filterObject });
			this.tableData = response.data.map((_: any) => new ServiceJobCategoryModel(_));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить категории сервисных работ`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузка категорий сервисных работ`,
					type: 'error',
				});
			}
			console.error('Error loading service-job-categories list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteJobCategoryAsync(categoryId: number) {
		try {
			this.tableLoading = true;
			const jwt = store.getters.jwt;
			const jobOperationsHttpClient = axiosHelper.createInstance(jwt);
			await jobOperationsHttpClient.delete(`/api/servicejobcategories/${categoryId}`);
			ElNotification({
				title: 'Категория сервисных работ удалена',
				type: 'success',
			});	
			EventHelper.dispatchEvent('jobCategoriesUpdate');
		} catch (e) {			
			ElNotification({
				title: 'Ошибка, категория сервисных работ не была удалена',
				type: 'error',
			});	
			console.error(`Error deleting service-job-category #${categoryId}`, e)
		} finally {
			this.tableLoading = false;
		}		
	}

	async onDeleteJobCategoryClick(categoryId: number, categoryName: string, e: Event) {
		e.stopPropagation();

		const relatedJobIdsToCategory = await this.getRelatedJobIdsAsync(categoryId);

		if (relatedJobIdsToCategory === null) {
			ElNotification({
				message: 'Ошибка удаления категории сервисных работ',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToCategory.length == 0) {
			ElMessageBox.confirm(
				`Категория сервисных работ '${categoryName}' будет удалена`,
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'error',
				}
			)
			.then(() => this.deleteJobCategoryAsync(categoryId));
		} else {
			ElMessageBox.alert(
				`Невозможно удалить категорию '${categoryName}', на нее ссылаются сервисные работы (${relatedJobIdsToCategory.length})`,
				'Удаление'
			);
		}
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobCategoriesAsync();
	}

	/** handles callback and passes selected category (if picker-mode is enabled) */	
	onPickJobCategoryClick(category: ServiceJobCategoryModel, e: Event) {
		e.stopPropagation();
		if (!this.onPickedCallback) {
			return;
		}
		this.onPickedCallback(category);
	}

	onCloseCurrentTabClick() {		
		const currentTabId = store.getters.tab;
		store.commit('removeTab', currentTabId);
	}

	async getRelatedJobIdsAsync(categoryId: number): Promise<Array<number> | null> {
		try {
			const jwt = store.getters.jwt;
			const jobCategoriesHttpClient = axiosHelper.createInstance(jwt);
			const jobCategoriesResponse = await jobCategoriesHttpClient.get(`/api/servicejobcategories/rels/${categoryId}`);
			return jobCategoriesResponse.data.map((_: number) => _);
		} catch (e) {			
			console.error(`Error checking service-job-category #${categoryId} for related jobs`, e);
			return null;
		}
	}
}
</script>